import { useEffect, useRef } from "react"

import { useLocation } from "react-router-dom"

import type { ContentSidebarPanel } from "@/stores/content-sidebar-store"

import { useContentSidebarStore } from "@/stores/content-sidebar-store"

import { ARTIFACT_PANEL_ID } from "../utils/artifact-panel-id"

/**
 * Closes the artifact preview pane when the user leaves the page it was opened
 * on: another module (e.g. Studio AI via "Open in editor"), another chat, a new
 * chat, or the library after a saved artifact (#3580). The content sidebar is a
 * global store, so without this the pane outlives its chat and shows an
 * unrelated, blank or wrong artifact.
 *
 * The owner is the router pathname when the panel object first appears. Only
 * the pathname is compared: same-path navigations (query cleanup, replace
 * redirects) keep the pane. Sending the first message of a new chat syncs the
 * router to `<chat path>/:sessionId` before the answer streams, so a pane that
 * auto-opens during that answer is owned by the chat's own path.
 *
 * Only the artifact panel is touched: the canvas deliberately survives
 * "Chat with document", and other modules own their panels.
 */
export function useCloseArtifactPanelOnRouteChange(): void {
  const { pathname } = useLocation()
  const currentPanel = useContentSidebarStore((state) => state.currentPanel)
  const forceClosePanel = useContentSidebarStore((state) => state.forceClosePanel)
  const ownerRef = useRef<{ panel: ContentSidebarPanel; pathname: string } | null>(null)

  useEffect(() => {
    // A page's own effect (which runs before this layout effect) may have just
    // opened a new panel for the new route. Defer to the re-render that brings
    // it, so a stale `currentPanel` never closes the freshly opened pane.
    if (useContentSidebarStore.getState().currentPanel !== currentPanel) return
    if (currentPanel?.id !== ARTIFACT_PANEL_ID) {
      ownerRef.current = null
      return
    }
    // A new panel object means a (re)open: it belongs to the current route,
    // even when the open landed in the same commit as a navigation.
    if (ownerRef.current?.panel !== currentPanel) {
      ownerRef.current = { panel: currentPanel, pathname }
      return
    }
    if (ownerRef.current.pathname !== pathname) {
      ownerRef.current = null
      forceClosePanel()
    }
  }, [currentPanel, pathname, forceClosePanel])
}
